import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, computed, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";

const RECENT_SEARCH_STORAGE_KEY = "bqa-inspection-item-search-recent-searches";
const MAX_RECENT_SEARCHES = 5;

export interface InspectionSearchItem {
  id: string;
  areaId: number;
  areaName: string;
  categoryName: string;
  assertionIdentifier: string;
  assertionText: string;
  isReviewed: boolean;
}

interface HighlightedTextSegment {
  text: string;
  isMatch: boolean;
}

@Component({
  selector: "app-inspection-item-search",
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule],
  templateUrl: "./inspection-item-search.component.html",
  styleUrl: "./inspection-item-search.component.scss"
})
export class InspectionItemSearchComponent {
  private readonly searchItems = signal<InspectionSearchItem[]>([]);
  private readonly recentSearchStorageKey = RECENT_SEARCH_STORAGE_KEY;
  private readonly maxRecentSearches = MAX_RECENT_SEARCHES;

  @Input() set items(value: InspectionSearchItem[] | null | undefined) {
    this.searchItems.set(value ?? []);
  }

  @Output() closeSearch = new EventEmitter<void>();
  @Output() selectSearchResult = new EventEmitter<InspectionSearchItem>();

  searchTerm = signal("");
  recentSearches = signal<string[]>(this.loadRecentSearches());

  trimmedSearchTerm = computed(() => this.searchTerm().trim());
  hasActiveSearch = computed(() => this.trimmedSearchTerm().length > 0);
  hasRecentSearches = computed(() => this.recentSearches().length > 0);

  categories = computed(() =>
    [...new Set(this.searchItems()
      .map(item => item.categoryName)
      .filter(categoryName => !!categoryName))]
      .sort((a, b) => a.localeCompare(b))
  );

  visibleCategories = computed(() => this.categories().slice(0, 6));

  filteredResults = computed(() => {
    const normalizedTerm = this.normalizeSearchText(this.trimmedSearchTerm());

    if (!normalizedTerm) {
      return [];
    }

    return this.searchItems()
      .filter(item =>
        this.normalizeSearchText(
          `${item.areaName} ${item.categoryName} ${this.stripHtml(item.assertionText)}`
        ).includes(normalizedTerm)
      )
      .sort((a, b) =>
        a.areaName.localeCompare(b.areaName) ||
        a.categoryName.localeCompare(b.categoryName) ||
        this.stripHtml(a.assertionText).localeCompare(this.stripHtml(b.assertionText))
      );
  });

  matchCount = computed(() => this.filteredResults().length);

  onSearchTermChange(value: string): void {
    this.searchTerm.set(value ?? "");
  }

  saveActiveSearch(): void {
    this.saveRecentSearch(this.trimmedSearchTerm());
  }

  clearSearch(): void {
    this.searchTerm.set("");
    setTimeout(() => document.getElementById("inspection-search-input")?.focus());
  }

  clearRecentSearches(): void {
    this.recentSearches.set([]);
    this.persistRecentSearches([]);
  }

  removeRecentSearch(searchTerm: string): void {
    const normalizedSearchTerm = this.normalizeSearchText(searchTerm);
    const updatedRecentSearches = this.recentSearches().filter(recentSearch =>
      this.normalizeSearchText(recentSearch) !== normalizedSearchTerm
    );

    this.recentSearches.set(updatedRecentSearches);
    this.persistRecentSearches(updatedRecentSearches);
  }

  close(): void {
    this.closeSearch.emit();
  }

  browseCategory(categoryName: string): void {
    this.searchTerm.set(categoryName);
    setTimeout(() => document.getElementById("inspection-search-input")?.focus());
  }

  selectRecentSearch(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
    this.saveRecentSearch(searchTerm);
    setTimeout(() => document.getElementById("inspection-search-input")?.focus());
  }

  selectResult(result: InspectionSearchItem): void {
    this.saveActiveSearch();
    this.selectSearchResult.emit(result);
  }

  getHighlightedSegments(value: string | null | undefined): HighlightedTextSegment[] {
    const text = this.stripHtml(value ?? "");
    const searchTerm = this.trimmedSearchTerm();

    if (!searchTerm) {
      return [{ text, isMatch: false }];
    }

    const escapedSearchTerm = this.escapeRegExp(searchTerm);
    const parts = text.split(new RegExp(`(${escapedSearchTerm})`, "ig"));

    return parts
      .filter(part => part.length > 0)
      .map(part => ({
        text: part,
        isMatch: this.normalizeSearchText(part) === this.normalizeSearchText(searchTerm),
      }));
  }

  private saveRecentSearch(searchTerm: string): void {
    const trimmedSearchTerm = searchTerm.trim();

    if (!trimmedSearchTerm) {
      return;
    }

    const normalizedSearchTerm = this.normalizeSearchText(trimmedSearchTerm);
    const updatedRecentSearches = [
      trimmedSearchTerm,
      ...this.recentSearches().filter(recentSearch =>
        this.normalizeSearchText(recentSearch) !== normalizedSearchTerm
      )
    ].slice(0, this.maxRecentSearches);

    this.recentSearches.set(updatedRecentSearches);
    this.persistRecentSearches(updatedRecentSearches);
  }

  private loadRecentSearches(): string[] {
    const localStorage = this.getLocalStorage();

    if (!localStorage) {
      return [];
    }

    try {
      const storedSearches = localStorage.getItem(this.recentSearchStorageKey);

      if (!storedSearches) {
        return [];
      }

      const parsedSearches = JSON.parse(storedSearches);

      if (!Array.isArray(parsedSearches)) {
        return [];
      }

      return parsedSearches.reduce((searches, search) => {
        if (typeof search !== "string") {
          return searches;
        }

        const trimmedSearch = search.trim();

        if (!trimmedSearch) {
          return searches;
        }

        const normalizedSearch = this.normalizeSearchText(trimmedSearch);
        const alreadyAdded = searches.some((existingSearch: string) =>
          this.normalizeSearchText(existingSearch) === normalizedSearch
        );

        if (!alreadyAdded) {
          searches.push(trimmedSearch);
        }

        return searches;
      }, [] as string[]).slice(0, this.maxRecentSearches);
    } catch {
      return [];
    }
  }

  private persistRecentSearches(searches: string[]): void {
    const localStorage = this.getLocalStorage();

    if (!localStorage) {
      return;
    }

    try {
      if (searches.length === 0) {
        localStorage.removeItem(this.recentSearchStorageKey);
        return;
      }

      localStorage.setItem(this.recentSearchStorageKey, JSON.stringify(searches));
    } catch {
      // Search still works without persistence.
    }
  }

  private getLocalStorage(): Storage | null {
    try {
      if (typeof window === "undefined" || !window.localStorage) {
        return null;
      }

      return window.localStorage;
    } catch {
      return null;
    }
  }

  private stripHtml(value: string): string {
    if (!value) {
      return "";
    }

    const element = document.createElement("div");
    element.innerHTML = value;

    return element.textContent || element.innerText || "";
  }

  private normalizeSearchText(value: string): string {
    return value.toLocaleLowerCase().trim();
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
}
